import type { SceneDefinition, SceneHotspot } from '../../scenes/scene.types';

export interface SimplifiedNav {
  element: HTMLElement;
  update(definition: SceneDefinition): void;
  setEnabled(enabled: boolean): void;
}

/**
 * Navigation simplifiée : une liste textuelle des actions de la scène.
 * Ce n'est pas une seconde application, seulement une autre porte d'entrée (§19).
 *
 * `onClose` est appelé quand le visiteur la referme depuis le panneau, par la
 * croix ou par un clic à l'extérieur : la commande qui l'a ouverte doit alors
 * repasser en position relâchée, sinon elle prétend le contraire de l'écran.
 */
export function createSimplifiedNav(
  onAction: (hotspot: SceneHotspot) => void,
  onClose?: () => void,
): SimplifiedNav {
  const element = document.createElement('section');
  element.className = 'simplified-nav';
  element.hidden = true;

  const title = document.createElement('h2');
  title.className = 'simplified-nav__title';
  title.textContent = 'Navigation simplifiée';

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'simplified-nav__close';
  close.setAttribute('aria-label', 'Fermer la navigation simplifiée');
  close.textContent = '×';
  close.addEventListener('click', () => onClose?.());

  const list = document.createElement('ul');
  list.className = 'simplified-nav__list';

  element.append(title, close, list);

  // Un clic hors du panneau le referme, comme la fiche produit. Le bouton qui
  // l'ouvre est exclu : sinon il refermerait puis rouvrirait dans la foulée.
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (element.hidden) return;
      const cible = event.target as HTMLElement | null;
      if (!cible) return;
      if (element.contains(cible) || cible.closest('[data-role="simplified-toggle"]')) return;
      onClose?.();
    },
    true,
  );

  return {
    element,
    update(definition: SceneDefinition): void {
      list.replaceChildren();
      for (const hotspot of definition.hotspots) {
        if (hotspot.enabled === false) continue;
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'simplified-nav__button';
        button.textContent = hotspot.simplifiedLabel ?? hotspot.label;
        button.addEventListener('click', () => onAction(hotspot));
        item.append(button);
        list.append(item);
      }
    },
    setEnabled(enabled: boolean): void {
      element.hidden = !enabled;
    },
  };
}
